import { isMobileDevice, isWeb } from "@stepapp/core";
import {
  Block,
  BottomSheet,
  Button,
  Divider,
  type IconNames,
  palette,
  SvgIcon,
  spacing,
  Touchable,
  Typography,
} from "@stepapp/ui";
import { usePathname, useRouter } from "expo-router";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Linking, Platform, Pressable, StyleSheet } from "react-native";
import { useAppLinks } from "../hooks";
import { AppStorage } from "../services/AppStorage";
import { useDisplayIntercom } from "../services/Intercom";

type NavigationItems = {
  label: string;
  onPress: () => void;
  icon: IconNames;
  isActive: boolean;
}[];

const BottomNavigation = () => {
  const { t } = useTranslation();
  const countryCode = AppStorage().get("countryCode");
  const displayIntercom = useDisplayIntercom(countryCode);
  const { navigate } = useRouter();
  const pathname = usePathname();
  const [sheetVisible, setSheetVisible] = useState(false);
  const { AppCustomer, AppLanding } = useAppLinks();

  const navigationItems = useMemo<NavigationItems>(
    () => [
      {
        label: t("components.tabBar.calendar"),
        onPress: () => navigate("/calendar/"),
        icon: "calendar",
        isActive: pathname === "/calendar",
      },
      {
        label: t("components.tabBar.dashboard"),
        onPress: () => navigate("/"),
        icon: "plus",
        isActive: pathname === "/",
      },
      {
        label: t("components.tabBar.myHome"),
        onPress: () => navigate("/my-home/"),
        icon: "my-home",
        isActive: pathname === "/my-home",
      },
      {
        label: t("components.drawer.help"),
        onPress: displayIntercom,
        icon: "chat-filled",
        isActive: false,
      },
    ],
    [pathname, navigate, displayIntercom, t],
  );

  if (isWeb) {
    navigationItems.push({
      label: t("components.tabBar.application"),
      onPress: () => setSheetVisible(true),
      icon: "offers-outline",
      isActive: false,
    });
  }

  const toggleSheetVisibility = useCallback(() => setSheetVisible((v) => !v), []);

  const items = useMemo(
    () =>
      navigationItems.map((item) => (
        <Block alignCenter key={item.label} minWidth={7.5} testID={`navigate-${item.label}`}>
          <Pressable key={item.label} onPress={() => item.onPress()}>
            <Block alignCenter marginBottom={0.5}>
              <SvgIcon
                color={item.isActive ? palette.primary.main : palette.font.secondary}
                name={item.icon}
                size={24}
              />
            </Block>
            <Typography type={item.isActive ? "primaryLink" : "secondary"} variant="navItem">
              {item.label}
            </Typography>
          </Pressable>
        </Block>
      )),
    [navigationItems],
  );

  const isVisible = pathname === "/my-home" || pathname === "/calendar" || pathname === "/";

  if (!isVisible) return null;

  return (
    <>
      <Block
        justifyAround
        row
        paddingHorizontal={3.5}
        paddingVertical={1.75}
        style={styles.container}
      >
        {items}
      </Block>

      <BottomSheet
        contentStyle={styles.bottomSheetHomeType}
        toggle={toggleSheetVisibility}
        visible={sheetVisible}
      >
        <Block justifySpace row marginBottom={2}>
          <Typography variant="newH2">{t("components.tabBar.stepappApp")}</Typography>

          <Touchable onPress={() => setSheetVisible(false)}>
            <Block style={styles.closeIcon}>
              <SvgIcon color={palette.secondary.grey} name="cross" size={38} />
            </Block>
          </Touchable>
        </Block>

        <Block marginBottom={3}>
          <Typography type="secondary">{t("components.tabBar.stepappAppDescription")}</Typography>
        </Block>

        <Divider spaceless />

        <Block alignCenter justifySpace row marginBottom={1} marginHorizontal={0.5} marginTop={3}>
          <Block alignCenter row marginBottom={2}>
            <Block alignCenter justifyCenter marginRight={1.5} style={styles.appIcon}>
              <Block style={styles.appIconBackground}>
                <SvgIcon color={palette.primary.accent} name="offers-filled" size={40} />
              </Block>
            </Block>
            <Typography variant="newH4">{t("components.tabBar.stepappMobileApp")}</Typography>
          </Block>

          <Button
            label={t("components.tabBar.openApp")}
            onPress={() => Linking.openURL(isMobileDevice ? AppCustomer : AppLanding)}
            type="secondary"
            variant="small"
          />
        </Block>
      </BottomSheet>
    </>
  );
};

export default BottomNavigation;

const styles = StyleSheet.create({
  appIcon: {
    backgroundColor: palette.primary.accent,
    borderRadius: 5,
    height: spacing.unit * 5,
    width: spacing.unit * 5,
  },
  appIconBackground: {
    backgroundColor: palette.primary.main,
  },
  bottomSheetHomeType: {
    bottom: 0,
    padding: spacing.unit * 2.5,
  },
  closeIcon: {
    backgroundColor: palette.common.black,
  },
  container: {
    backgroundColor: palette.common.white,
    borderRadius: 50,
    bottom: Platform.select({ default: 20, android: 30 }),
    minWidth: spacing.unit * 40,
    position: "absolute",
    zIndex: 2,
    ...Platform.select({
      default: {
        elevation: 5,
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.2,
        shadowRadius: 13,
      },
      web: { boxShadow: "0px 8px 13px rgba(0, 0, 0, 0.1)" },
    }),
  },
});
